import { ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import { ComponentProps } from 'lib/component-props';
import { Forms } from 'lib/templates/Feature.Dart.model';
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { rewritePathByHost } from 'lib/utils/rewrite-helper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';

export type SCJBrandsContactFormProps = ComponentProps &
  Forms.ScjbrandsContact.ScjBrandsContactForm;
const nameLabelMappings: Record<string, string> = {};

interface FormFieldProps {
  label?: string;
  required?: boolean;
  show?: boolean;
  data?: Forms.ScjbrandsContact.SelectListItem[];
  name: string;
  type?: 'text' | 'select' | 'checkbox' | 'textarea';
  helpImage?: ImageField;
  fristOptionText?: string;
  error?: string;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onInput?: (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  show = true,
  data,
  name,
  type = 'text',
  helpImage,
  error,
  onBlur,
  onInput,
}) => {
  if (!show) return null;

  const fieldId = `field-${name}`;

  nameLabelMappings[name] = label ?? '';

  const getInputProps = () => {
    const baseProps = {
      id: fieldId,
      name,
      required,
      className: `border rounded p-2 w-full ${error ? 'border-red-500' : ''}`,
      onBlur,
      onInput,
    };

    if (type === 'text' || type === 'textarea') {
      if (name === 'Zip_Postal_Code') {
        return {
          ...baseProps,
          inputMode: 'text' as const, // this can be letters in other countries
          autoComplete: 'postal-code',
        };
      }

      if (name === 'Email' || name === 'Verify_Email') {
        return {
          ...baseProps,
          type: 'email' as const,
          autoComplete: name === 'Email' ? 'email' : 'off',
        };
      }

      if (name === 'Phone') {
        return {
          ...baseProps,
          inputMode: 'tel' as const,
          autoComplete: 'tel',
        };
      }

      return {
        ...baseProps,
        type: 'text' as const,
      };
    }

    if (type === 'select') {
      return baseProps;
    }

    if (type === 'checkbox') {
      return {
        ...baseProps,
        type: 'checkbox' as const,
        className: 'h-4 w-4',
      };
    }

    return baseProps;
  };

  const renderField = () => {
    switch (type) {
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <input {...getInputProps()} />
            {label && (
              <label
                htmlFor={fieldId}
                className="text-sm"
                dangerouslySetInnerHTML={{
                  __html: (required ? '<span class="text-red-500">*</span> ' : '') + (label ?? ''),
                }}
              />
            )}
          </div>
        );
      case 'select':
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className="block mb-1 font-medium">
                {required && <span className="text-red-500">* </span>}
                {label}
              </label>
            )}
            <select {...getInputProps()}>
              {data?.map((option, index) => (
                <option
                  key={index}
                  value={option.fields?.value?.value}
                  disabled={option.fields?.disabled?.value}
                >
                  {option.fields?.name?.value}
                </option>
              ))}
            </select>
          </>
        );
      case 'textarea':
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className="block mb-1 font-medium">
                {required && <span className="text-red-500">* </span>}
                {label}
              </label>
            )}
            <textarea {...getInputProps()} rows={5} />
          </>
        );
      default:
        return (
          <>
            {label && (
              <label htmlFor={fieldId} className="block mb-1 font-medium">
                {required && <span className="text-red-500">* </span>}
                {label}
                {helpImage && (
                  <span className="relative group ml-2 cursor-pointer inline-block align-middle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-blue-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-4a1 1 0 110 2 1 1 0 010-2zm1 4a1 1 0 00-2 0v4a1 1 0 002 0v-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="absolute z-10 hidden group-hover:block bg-white border border-gray-300 shadow-lg rounded p-2 mt-2 w-64">
                      <ImageWrapper field={helpImage} className="max-w-full max-h-48 mx-auto" />
                    </div>
                  </span>
                )}
              </label>
            )}
            <input {...getInputProps()} />
          </>
        );
    }
  };

  return (
    <div className="form-field mb-4">
      {renderField()}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const SCJBrandsContactForm = (props: SCJBrandsContactFormProps): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isValid, setIsValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const router = useRouter();

  const [_referrer, setReferrer] = useState<string | null>(null);

  useEffect(() => {
    setReferrer(document.referrer);
  }, []);

  function getFormattedDateTime() {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());

    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const validateField = (name: string, value: string, isRequired: boolean): string => {
    if (isRequired && !value.trim()) {
      return 'This field is required.';
    }

    if ((name === 'Email' || name === 'Verify_Email') && value) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!isValidEmail) {
        return props.fields?.emailNotValidErrorMessage?.value || 'Invalid email address';
      }
    }

    return '';
  };

  const handleInput = () => {
    if (formRef.current) {
      setIsValid(formRef.current.checkValidity());
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, required } = e.target;
    const form = formRef.current;
    const updatedErrors = { ...formErrors };

    const fieldError = validateField(name, value, required);
    if (fieldError) {
      updatedErrors[name] = fieldError;
    } else {
      delete updatedErrors[name];
    }

    if ((name === 'Email' || name === 'Verify_Email') && form) {
      const email = form.email?.value;
      const verifyEmail = form.verifyEmail?.value;
      if (email && verifyEmail && email !== verifyEmail) {
        updatedErrors.verifyEmail =
          props.fields?.emailsDoNotMatchErrorMessage?.value || 'Emails do not match';
      } else {
        delete updatedErrors.verifyEmail;
      }
    }

    setFormErrors(updatedErrors);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.email?.value;
    const verifyEmail = form.verifyEmail?.value;

    const formData: Record<string, string | boolean> = {};
    const newErrors: Record<string, string> = {};

    for (const element of form.elements) {
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ) {
        if (element instanceof HTMLInputElement && element.type === 'checkbox') {
          formData[element.name] = element.checked;
        } else {
          formData[element.name] = element.value;
        }

        const error = validateField(element.name, element.value, element.required);
        if (error) newErrors[element.name] = error;
      }
    }

    if (email !== verifyEmail) {
      newErrors.verifyEmail =
        props.fields?.emailsDoNotMatchErrorMessage?.value || 'Emails do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      const firstErrorField = form.querySelector(`[name="${Object.keys(newErrors)[0]}"]`);
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFormErrors({});
    let toEmail = props?.fields?.emailTo?.value;
    if (formData['Subject']?.toString().toUpperCase().includes('B2B')) {
      toEmail = props?.fields?.emailB2B?.value;
    }
    const emailSubject = formData['Subject'] + ' Contact Form Submission';
    let emailMsg = 'From Address: ' + props?.fields?.emailFrom?.value + '\r\n';
    emailMsg += 'From Email Id: ' + props?.fields?.emailFrom?.value + '\r\n';
    emailMsg += 'Received: ' + getFormattedDateTime() + '\r\n';
    emailMsg += 'To Addresses: ' + toEmail + '\r\n';
    emailMsg += 'CC Addresses: ' + props?.fields?.emailCC?.value + '\r\n';
    emailMsg += 'Subject: ' + emailSubject + '\r\n';
    for (const [name, _label] of Object.entries(nameLabelMappings)) {
      let sep = '';
      let emailLabel = name;
      if (emailLabel === 'Comments') {
        sep = '\r\n';
      } else if (emailLabel === 'Subject') {
        continue;
      } else if (emailLabel === 'State_Province') {
        emailLabel = 'State/Province';
      } else if (emailLabel === 'Zip_Postal_Code') {
        emailLabel = 'Zip/Postal Code';
      }
      emailLabel = emailLabel.replace('_', ' ');

      let formValue = formData[name];
      if (name === 'Read_Terms' || name == 'Permission') {
        formValue = formValue ? 'Yes' : 'No';
      }

      if (name === 'Read_Terms') {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
        emailMsg += 'Webform: ' + props?.fields?.formTitle?.value + '\r\n';
        emailMsg += 'Website: ' + currentUrl + '\r\n';
      }

      emailMsg += `${emailLabel}: ${sep}${sep}${formValue}${sep}\r\n`;
    }

    //console.log(emailMsg);

    try {
      const response = await fetch('/api/ScjBrandsContact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: emailMsg,
          from: props?.fields?.emailFrom?.value,
          subject: emailSubject,
          to: toEmail,
          cc: props?.fields?.emailCC?.value,
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (props.fields?.confirmationPage?.value?.href) {
          const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
          router.push(
            rewritePathByHost(props.fields?.confirmationPage?.value?.href, currentUrl) ?? ''
          );
        }
      } else {
        console.error('Email sending failed:', result.error);
      }
    } catch (err) {
      console.error('Error sending email:', err);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6 p-4">
      {props.fields?.formTitle?.value && <h2>{props.fields?.formTitle?.value}</h2>}

      <RichTextA11yWrapper field={props.fields?.formIntroduction} />

      {_referrer && <p className="mt-1 text-sm text-green-600">{_referrer} for moneyback</p>}

      {props.fields?.requiredFieldsLabel?.value && (
        <p className="mt-1 text-sm text-red-600">{props.fields?.requiredFieldsLabel?.value}</p>
      )}

      <FormField
        name="Title"
        label={props.fields?.titleLabel?.value}
        required={props.fields?.titleRequired?.value}
        show={props.fields?.titleShown?.value}
        data={props.fields?.titleData}
        type="select"
        error={formErrors.title}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="First_Name"
        label={props.fields?.firstNameLabel?.value}
        required={props.fields?.firstNameRequired?.value}
        show={props.fields?.firstNameShown?.value}
        error={formErrors.firstName}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Last_Name"
        label={props.fields?.lastNameLabel?.value}
        required={props.fields?.lastNameRequired?.value}
        show={props.fields?.lastNameShown?.value}
        error={formErrors.lastName}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Address"
        label={props.fields?.addressLabel?.value}
        required={props.fields?.addressRequired?.value}
        show={props.fields?.addressShown?.value}
        error={formErrors.address}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Address2"
        label={props.fields?.addressApartmentLabel?.value}
        required={props.fields?.addressApartmentRequired?.value}
        show={props.fields?.addressApartmentShown?.value}
        error={formErrors.addressApartment}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="City"
        label={props.fields?.cityLabel?.value}
        required={props.fields?.cityRequired?.value}
        show={props.fields?.cityShown?.value}
        error={formErrors.city}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="State_Province"
        label={props.fields?.stateLabel?.value}
        required={props.fields?.stateRequired?.value}
        show={props.fields?.stateShown?.value}
        data={props.fields?.stateData}
        type="select"
        error={formErrors.state}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Zip_Postal_Code"
        label={props.fields?.zipLabel?.value}
        required={props.fields?.zipRequired?.value}
        show={props.fields?.zipShown?.value}
        error={formErrors.zipCode}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Country"
        label={props.fields?.countryLabel?.value}
        required={props.fields?.countryRequired?.value}
        show={props.fields?.countryShown?.value}
        data={props.fields?.countryData}
        type="select"
        error={formErrors.country}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Email"
        label={props.fields?.emailLabel?.value}
        required={props.fields?.emailRequired?.value}
        show={props.fields?.emailShown?.value}
        error={formErrors.email}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Verify_Email"
        label={props.fields?.verifyEmailLabel?.value}
        required={props.fields?.verifyEmailRequired?.value}
        show={props.fields?.verifyEmailShown?.value}
        error={formErrors.verifyEmail}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Phone"
        label={props.fields?.phoneLabel?.value}
        required={props.fields?.phoneRequired?.value}
        show={props.fields?.phoneShown?.value}
        error={formErrors.phone}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Subject"
        label={props.fields?.subjectLabel?.value}
        required={props.fields?.subjectRequired?.value}
        show={props.fields?.subjectShown?.value}
        data={props.fields?.subjectData}
        type="select"
        error={formErrors.subject}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Regarding"
        label={props.fields?.regardingLabel?.value}
        required={props.fields?.regardingRequired?.value}
        show={props.fields?.regardingShown?.value}
        data={props.fields?.regardingData}
        type="select"
        error={formErrors.regarding}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Drain_Type"
        label={props.fields?.regardingSecondaryLabel?.value}
        required={props.fields?.regardingSecondaryRequired?.value}
        show={props.fields?.regardingSecondaryShown?.value}
        data={props.fields?.regardingSecondaryData}
        type="select"
        error={formErrors.regarding}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="UPC"
        label={props.fields?.productUpcLabel?.value}
        required={props.fields?.productUpcRequired?.value}
        show={props.fields?.productUpcShown?.value}
        error={formErrors.productUPC}
        helpImage={props.fields?.productUpcHelpImage}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Batch_Code"
        label={props.fields?.batchCodeLabel?.value}
        required={props.fields?.batchCodeRequired?.value}
        show={props.fields?.batchCodeShown?.value}
        error={formErrors.batchCode}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Comments"
        label={props.fields?.commentLabel?.value}
        required={props.fields?.commentRequired?.value}
        show={props.fields?.commentShown?.value}
        type="textarea"
        error={formErrors.comment}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Permission"
        label={props.fields?.contactConsentLabel?.value}
        required={props.fields?.contactConsentRequired?.value}
        show={props.fields?.contactConsentShown?.value}
        type="checkbox"
        error={formErrors.contactConsent}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <FormField
        name="Read_Terms"
        label={props.fields?.termsAndPrivacyConsentLabel?.value}
        required={props.fields?.termsAndPrivacyConsentRequired?.value}
        show={props.fields?.termsAndPrivacyConsentShown?.value}
        type="checkbox"
        error={formErrors.termsAndPrivacyConsent}
        onBlur={handleBlur}
        onInput={handleInput}
      />
      <button
        type="submit"
        className={`px-6 py-2 rounded transition ${
          isValid
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-400 text-white cursor-not-allowed'
        }`}
        disabled={!isValid}
      >
        {props?.fields?.submitButtonLabel?.value}
      </button>
      <RichTextA11yWrapper field={props.fields?.formFooter} />
    </form>
  );
};

export default SCJBrandsContactForm;
